import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label } from '../components/ui'
import { CheckCircle, ArrowLeft } from 'lucide-react'

interface InvitationDetails {
  id: string
  team_name: string
  inviter_name: string
  role: string
  contact: string
  contact_type: 'email' | 'phone'
  expires_at: number
}

export default function InvitationOTPVerification() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  
  const phoneNumber = searchParams.get('phone')
  const inviteToken = searchParams.get('invite')

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // Load invitation details
  useEffect(() => {
    if (inviteToken) {
      loadInvitationDetails()
    }
  }, [inviteToken])

  const loadInvitationDetails = async () => {
    if (!inviteToken) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${inviteToken}`
      )

      if (response.ok) {
        const data = await response.json()
        setInvitation(data.invitation)
      }
    } catch (err) {
      console.error('Failed to load invitation:', err)
    }
  }

  if (!phoneNumber) {
    navigate('/login')
    return null
  }

  if (!inviteToken) {
    navigate('/auth/verify-otp?phone=' + encodeURIComponent(phoneNumber))
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      console.log(`Verifying OTP for phone: "${phoneNumber}"`)
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/auth/phone/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber,
          otp
        })
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('auth_token', data.token)
        
        // Automatically accept the invitation
        try {
          console.log(`Accepting invitation ${inviteToken} for user after phone auth`)
          console.log(`User authenticated with phone: "${phoneNumber}"`)
          const acceptResponse = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${inviteToken}/accept`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${data.token}`
              }
            }
          )

          if (acceptResponse.ok) {
            // Successful authentication and invitation acceptance
            console.log('Invitation accepted successfully, redirecting to dashboard')
            window.location.href = '/dashboard?invitation_accepted=true'
            return
          } else {
            const errorData = await acceptResponse.json()
            console.error('Failed to accept invitation after phone auth:', errorData)
            alert(`Failed to accept invitation: ${errorData.error || 'Unknown error'}`)
            // Still redirect to dashboard but show error
            window.location.href = '/dashboard?invite_error=true'
            return
          }
        } catch (inviteErr) {
          console.error('Error accepting invitation after phone auth:', inviteErr)
          alert('Authentication succeeded but failed to accept invitation. Please try again.')
          window.location.href = '/dashboard?invite_error=true'
          return
        }
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Invalid verification code. Please try again.')
      }
    } catch (error) {
      console.error('Verification failed:', error)
      alert('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setResending(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/auth/phone/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber
        })
      })

      if (response.ok) {
        setCountdown(60)
        alert('Verification code sent!')
      } else {
        alert('Failed to resend code. Please try again.')
      }
    } catch (error) {
      console.error('Resend failed:', error)
      alert('Failed to resend code. Please try again.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header with company branding */}
        {invitation && (
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Join {invitation.team_name}</h1>
            <p className="mt-2 text-sm text-gray-600">
              Complete verification to join your team
            </p>
          </div>
        )}

        {/* Invitation Details Card */}
        {invitation && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center mb-3">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">Team Invitation</span>
              </div>
              <div className="text-sm text-blue-800 space-y-1">
                <div className="flex justify-between">
                  <span>Company:</span>
                  <span className="font-medium">{invitation.team_name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Role:</span>
                  <span className="font-medium">{invitation.role}</span>
                </div>
                <div className="flex justify-between">
                  <span>Invited by:</span>
                  <span className="font-medium">{invitation.inviter_name}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Verify Your Phone</CardTitle>
            <CardDescription>
              We sent a 6-digit code to {phoneNumber}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg tracking-widest"
                  maxLength={6}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || otp.length !== 6}
              >
                {loading ? 'Verifying & Joining Team...' : 'Verify & Join Team'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-4">
              <div>
                <p className="text-sm text-gray-600">
                  Didn't receive a code?{' '}
                  {countdown > 0 ? (
                    <span>Resend in {countdown}s</span>
                  ) : (
                    <button
                      onClick={handleResendOTP}
                      disabled={resending}
                      className="text-blue-600 hover:text-blue-500 font-medium"
                    >
                      {resending ? 'Sending...' : 'Resend code'}
                    </button>
                  )}
                </p>
              </div>
              
              <button
                onClick={() => navigate(`/invite/${inviteToken}/login`)}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to sign in options
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}